import { GraphQLError } from 'graphql';
import { AxiosError } from 'axios';
import { getErrorCodeMessage } from '@/common/domain/constants/errorCodes';
import { formatValidationMessage } from './fieldNameFormatter';

/**
 * Error structure from backend REST API responses
 */
interface BackendError {
  code?: string;
  message?: string;
  errorMessage?: string;
  errorType?: string | number;
  statusCode?: string | number;
  details?: Array<{ path?: string; reason?: string; field?: string; message?: string }>;
  validationErrors?: Array<{ field?: string; message?: string }>;
}

/**
 * GraphQL error extensions structure
 * Matches the format used by our GraphQL backend
 */
interface GraphQLErrorExtensions {
  code?: string;
  errorType?: string | number;
  statusCode?: string | number;
  errorMessage?: string;
  validationErrors?: Array<string | { field?: string; message?: string }>;
  [key: string]: unknown;
}

/**
 * Formatted error result with title and message
 */
export interface FormattedError {
  title: string;
  message: string;
  code?: string;
  details?: BackendError['details'];
}

/**
 * Extracts error code from various error types
 *
 * Supports:
 * - GraphQL errors with extensions
 * - Axios errors with response data
 * - REST API error objects
 * - Apollo Client error structures
 *
 * @param error - The error to extract code from
 * @returns The error code string or undefined if not found
 * @internal
 */
const extractErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  // GraphQL errors with extensions
  if (error instanceof GraphQLError) {
    const graphQLError = error as GraphQLError;
    const extensions = graphQLError.extensions as GraphQLErrorExtensions | undefined;
    return extensions?.code || extensions?.errorType?.toString() || extensions?.statusCode?.toString();
  }

  // Axios errors
  if (error instanceof AxiosError) {
    const axiosError = error as AxiosError;
    const data = axiosError.response?.data as Record<string, unknown> | undefined;
    // Check for nested error object (common in REST APIs)
    if (data?.error && typeof data.error === 'object') {
      const nestedError = data.error as BackendError;
      if (nestedError.code) {
        return nestedError.code;
      }
    }
    // Check direct code property
    const backendError = data as BackendError | undefined;
    if (backendError?.code) {
      return backendError.code;
    }
    // Use HTTP status code as fallback
    if (axiosError.response?.status) {
      return axiosError.response.status.toString();
    }
  }

  // Generic error objects
  const errorObj = error as Record<string, unknown>;

  // Check for nested error object (common in REST APIs)
  if (errorObj.error && typeof errorObj.error === 'object') {
    const nestedError = errorObj.error as BackendError;
    if (nestedError.code) {
      return nestedError.code;
    }
  }

  // Check direct properties
  if (errorObj.code) {
    return String(errorObj.code);
  }

  // Check GraphQL-style extensions
  if (errorObj.extensions) {
    const extensions = errorObj.extensions as GraphQLErrorExtensions;
    return extensions.code || extensions.errorType?.toString() || extensions.statusCode?.toString();
  }

  // Check for GraphQL errors array
  if (errorObj.graphQLErrors && Array.isArray(errorObj.graphQLErrors)) {
    const firstError = errorObj.graphQLErrors[0] as GraphQLError | undefined;
    if (firstError?.extensions) {
      const extensions = firstError.extensions as GraphQLErrorExtensions;
      return extensions.code || extensions.errorType?.toString() || extensions.statusCode?.toString();
    }
  }

  // Check networkError.result.errors (Apollo Client structure)
  if (errorObj.networkError) {
    const networkError = errorObj.networkError as Record<string, unknown>;
    if (networkError.result) {
      const result = networkError.result as { errors?: Array<GraphQLError> };
      if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        const firstError = result.errors[0];
        if (firstError && firstError.extensions) {
          const extensions = firstError.extensions as GraphQLErrorExtensions;
          return extensions.code || extensions.errorType?.toString() || extensions.statusCode?.toString();
        }
      }
    }
  }

  return undefined;
};

/**
 * Extracts raw error message from various error types
 *
 * Attempts to extract the original error message before formatting.
 * Checks multiple locations where error messages might be stored.
 *
 * @param error - The error to extract message from
 * @returns The raw error message or undefined if not found
 * @internal
 */
const extractRawErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined;
  }

  // GraphQL errors
  if (error instanceof GraphQLError) {
    const graphQLError = error as GraphQLError;
    const extensions = graphQLError.extensions as GraphQLErrorExtensions | undefined;
    return extensions?.errorMessage || graphQLError.message;
  }

  // Axios errors
  if (error instanceof AxiosError) {
    const axiosError = error as AxiosError;
    const data = axiosError.response?.data as Record<string, unknown> | undefined;
    // Check for nested error object (common in REST APIs)
    if (data?.error && typeof data.error === 'object') {
      const nestedError = data.error as BackendError;
      if (nestedError.errorMessage) {
        return nestedError.errorMessage;
      }
      if (nestedError.message) {
        return nestedError.message;
      }
    }
    // Check direct properties
    const backendError = data as BackendError | undefined;
    if (backendError?.errorMessage) {
      return backendError.errorMessage;
    }
    if (backendError?.message) {
      return backendError.message;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Generic error objects
  const errorObj = error as Record<string, unknown>;

  // Check for nested error object (common in REST APIs)
  if (errorObj.error && typeof errorObj.error === 'object') {
    const nestedError = errorObj.error as BackendError;
    if (nestedError.errorMessage) {
      return nestedError.errorMessage;
    }
    if (nestedError.message) {
      return nestedError.message;
    }
  }

  // Check networkError.result.errors (Apollo Client structure) - highest priority for network errors
  if (errorObj.networkError) {
    const networkError = errorObj.networkError as Record<string, unknown>;
    if (networkError.result) {
      const result = networkError.result as { errors?: Array<GraphQLError>; message?: string };
      if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        const firstError = result.errors[0];
        if (firstError && firstError.message) {
          return firstError.message;
        }
      }
      if (result.message) {
        return result.message;
      }
    }
    if (networkError.message && typeof networkError.message === 'string') {
      return networkError.message;
    }
  }

  // Check GraphQL errors array
  if (errorObj.graphQLErrors && Array.isArray(errorObj.graphQLErrors)) {
    const firstError = errorObj.graphQLErrors[0] as GraphQLError | undefined;
    if (firstError?.message) {
      return firstError.message;
    }
  }

  // Check direct message property (lowest priority)
  if (errorObj.message && typeof errorObj.message === 'string') {
    return errorObj.message;
  }

  return undefined;
};

/**
 * Parses a validation error string in the format "{ field = X, message = Y }"
 * @param validationErrorString - The validation error string to parse
 * @returns Object with field and message, or undefined if parsing fails
 * @internal
 */
const parseValidationErrorString = (validationErrorString: string): { field?: string; message?: string } | undefined => {
  if (typeof validationErrorString !== 'string') {
    return undefined;
  }

  // Match format: "{ field = JobSiteName, message = \"JobSite Name cannot be empty, or it contains invalid characters\" }"
  const match = validationErrorString.match(/\{\s*field\s*=\s*([^,]+),\s*message\s*=\s*([^}]+)\s*\}/);
  if (match) {
    let message = match[2]?.trim() || '';
    // Only remove quotes if the entire message is wrapped in matching quotes
    // This prevents removing quotes that are part of the message content
    const firstChar = message[0];
    const lastChar = message[message.length - 1];
    if ((firstChar === '"' && lastChar === '"') || (firstChar === "'" && lastChar === "'")) {
      message = message.slice(1, -1);
    }
    return {
      field: match[1]?.trim(),
      message
    };
  }

  return undefined;
};

/**
 * Extracts validation details from error
 *
 * Handles both `details` array format and `validationErrors` array format,
 * normalizing them to a consistent structure.
 * Also handles GraphQL extensions.validationErrors with string format.
 *
 * @param error - The error to extract validation details from
 * @returns Array of validation details or undefined if not found
 * @internal
 */
const extractValidationDetails = (error: unknown): BackendError['details'] => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorObj = error as Record<string, unknown>;

  // Helper function to extract validation errors from extensions
  const extractFromExtensions = (extensions: GraphQLErrorExtensions | undefined): BackendError['details'] => {
    if (extensions?.validationErrors && Array.isArray(extensions.validationErrors)) {
      return extensions.validationErrors.map((ve) => {
        // Handle string format: "{ field = X, message = Y }"
        if (typeof ve === 'string') {
          const parsed = parseValidationErrorString(ve);
          if (parsed) {
            return {
              path: parsed.field,
              reason: parsed.message && formatValidationMessage(parsed.message, parsed.field)
            };
          }
          // Fallback: treat entire string as message
          return {
            reason: formatValidationMessage(ve)
          };
        }
        // Handle object format: { field: X, message: Y }
        return {
          path: ve.field,
          reason: ve.message && formatValidationMessage(ve.message, ve.field)
        };
      });
    }
    return undefined;
  };

  // Check networkError.result.errors (Apollo Client structure) - highest priority
  if (errorObj.networkError) {
    const networkError = errorObj.networkError as Record<string, unknown>;
    if (networkError.result) {
      const result = networkError.result as { errors?: Array<{ extensions?: GraphQLErrorExtensions }> };
      if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        // Check all errors in the array for validation errors
        for (const err of result.errors) {
          if (err.extensions) {
            const validationDetails = extractFromExtensions(err.extensions);
            if (validationDetails) {
              return validationDetails;
            }
          }
        }
      }
    }
  }

  // Check graphQLErrors array (Apollo Client structure)
  if (errorObj.graphQLErrors && Array.isArray(errorObj.graphQLErrors)) {
    for (const err of errorObj.graphQLErrors) {
      if (err && typeof err === 'object') {
        const graphQLError = err as { extensions?: GraphQLErrorExtensions };
        const validationDetails = extractFromExtensions(graphQLError.extensions);
        if (validationDetails) {
          return validationDetails;
        }
      }
    }
  }

  // Check GraphQL extensions for validationErrors (GraphQLError instance)
  if (error instanceof GraphQLError) {
    const graphQLError = error as GraphQLError;
    const validationDetails = extractFromExtensions(graphQLError.extensions as GraphQLErrorExtensions | undefined);
    if (validationDetails) {
      return validationDetails;
    }
  }

  // Check for GraphQL-style extensions in generic error objects
  if (errorObj.extensions) {
    const validationDetails = extractFromExtensions(errorObj.extensions as GraphQLErrorExtensions);
    if (validationDetails) {
      return validationDetails;
    }
  }

  // Check for nested error object
  if (errorObj.error && typeof errorObj.error === 'object') {
    const nestedError = errorObj.error as BackendError;
    if (nestedError.details) {
      return nestedError.details;
    }
    if (nestedError.validationErrors) {
      return nestedError.validationErrors.map((ve) => ({
        path: ve.field,
        reason: ve.message && formatValidationMessage(ve.message, ve.field)
      }));
    }
  }

  // Check direct properties
  if (errorObj.details) {
    return errorObj.details as BackendError['details'];
  }

  if (errorObj.validationErrors) {
    const validationErrors = errorObj.validationErrors as BackendError['validationErrors'];
    return validationErrors?.map((ve) => ({
      path: ve.field,
      reason: ve.message && formatValidationMessage(ve.message, ve.field)
    }));
  }

  return undefined;
};

/**
 * Extracts an RFC 7807 `ValidationProblemDetails` shape from an Axios error response.
 *
 * Servers built on ASP.NET (or any RFC 7807-compliant stack) return:
 *
 * ```json
 * {
 *   "type": "...",
 *   "title": "One or more validation errors occurred.",
 *   "status": 400,
 *   "errors": { "Justification": ["Justification must be between 10 and 1000 characters."] }
 * }
 * ```
 *
 * The legacy `extractValidationDetails` helper does not understand the `errors` *dict* shape (it
 * looks for `details` / `validationErrors` arrays), and the legacy `extractRawErrorMessage` falls
 * back to Axios's generic `"Request failed with status code 400"` instead of the BE's actual rule.
 * This helper bridges both gaps so the snackbar surfaces the BE message verbatim — see
 * `approvals-frontend-plan.md` Bug A.5.
 *
 * Preference order (per the FE plan):
 *  1. `errors[firstField][0]` (first field's first message)
 *  2. `detail`
 *  3. `title`
 *
 * @returns `{ message, details? }` when a usable shape is found; `undefined` otherwise so callers
 * can fall back to the existing extraction paths.
 * @internal
 */
const extractRfc7807FromAxios = (error: unknown): { message: string; details?: BackendError['details'] } | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined;
  }

  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  // 1. `errors` dict — flatten every `(field, msg)` pair so the validation list shows them all,
  // and use the first one as the headline message.
  const errorsField = data.errors;
  if (errorsField && typeof errorsField === 'object' && !Array.isArray(errorsField)) {
    const flat: NonNullable<BackendError['details']> = [];
    for (const [field, msgs] of Object.entries(errorsField as Record<string, unknown>)) {
      if (Array.isArray(msgs)) {
        for (const msg of msgs) {
          if (typeof msg === 'string' && msg.trim() !== '') {
            flat.push({ path: field, reason: msg });
          }
        }
      } else if (typeof msgs === 'string' && msgs.trim() !== '') {
        // Some servers serialize a single error as a string instead of an array.
        flat.push({ path: field, reason: msgs });
      }
    }
    const first = flat[0];
    if (first?.reason) {
      return { message: first.reason, details: flat };
    }
  }

  // 2. `detail` — RFC 7807's free-text description of the problem instance.
  if (typeof data.detail === 'string' && data.detail.trim() !== '') {
    return { message: data.detail };
  }

  // 3. `title` — RFC 7807's short, human-readable summary (lowest priority).
  if (typeof data.title === 'string' && data.title.trim() !== '') {
    return { message: data.title };
  }

  return undefined;
};

/**
 * Formats an error into a user-friendly message with title
 *
 * Supports:
 * - Magic codes (error codes mapped to user-friendly messages)
 * - GraphQL extensions (code, errorType, statusCode)
 * - REST API error structures (nested error objects)
 * - Apollo Client error structures
 * - Standard Error objects
 *
 * @param error - The error to format (can be GraphQLError, AxiosError, Error, or any error-like object)
 * @param defaultMessage - Default message if no error message is found (default: 'An unexpected error occurred. Please try again.')
 * @param defaultTitle - Default title if no error title is found (default: 'Error')
 * @returns Formatted error with title, message, optional code, and optional validation details
 *
 * @example
 * ```ts
 * try {
 *   await createEquipment(data);
 * } catch (error) {
 *   const formatted = formatError(error, 'Failed to create equipment', 'Creation Error');
 *   showSnackbar(formatted.title, formatted.message, 'error');
 * }
 * ```
 *
 * @example
 * ```ts
 * // With error code mapping
 * const error = new GraphQLError('Validation failed', {
 *   extensions: { code: 'VALIDATION_ERROR' }
 * });
 * const formatted = formatError(error);
 * // Returns: { title: 'Validation Error', message: 'Please review the highlighted fields...', code: 'VALIDATION_ERROR' }
 * ```
 */
export const formatError = (
  error: unknown,
  defaultMessage: string = 'An unexpected error occurred. Please try again.',
  defaultTitle: string = 'Error'
): FormattedError => {
  if (!error) {
    return {
      title: defaultTitle,
      message: defaultMessage
    };
  }

  // Extract error code (magic code)
  const errorCode = extractErrorCode(error);

  // If we have a mapped error code, use it
  if (errorCode) {
    const codeMapping = getErrorCodeMessage(errorCode);
    if (codeMapping) {
      return {
        title: codeMapping.title || defaultTitle,
        message: codeMapping.message,
        code: errorCode,
        details: extractValidationDetails(error) ?? extractRfc7807FromAxios(error)?.details
      };
    }
  }

  // RFC 7807 `ValidationProblemDetails` — surface the BE's exact rule (`errors`/`detail`/`title`)
  // before falling through to the generic "Please review the highlighted field" path. This makes
  // future BE rule changes show up in the snackbar without any FE work — see
  // `approvals-frontend-plan.md` Bug A.5.
  const rfc7807 = extractRfc7807FromAxios(error);
  if (rfc7807) {
    return {
      title: errorCode ? `${defaultTitle} (${errorCode})` : defaultTitle,
      message: rfc7807.message,
      code: errorCode,
      details: rfc7807.details
    };
  }

  // Extract validation details first to check if we should use them for the message
  const validationDetails = extractValidationDetails(error);

  // If we have validation details, use a generic message to avoid duplication
  // The specific errors will be shown in the ValidationErrorsList component
  if (validationDetails && validationDetails.length > 0) {
    const errorCount = validationDetails.length;
    const genericMessage =
      errorCount === 1 ? 'Please review the highlighted field.' : `Please review the ${errorCount} highlighted fields.`;

    return {
      title: errorCode ? `${defaultTitle} (${errorCode})` : defaultTitle,
      message: genericMessage,
      code: errorCode,
      details: validationDetails
    };
  }

  // Extract raw error message
  const rawMessage = extractRawErrorMessage(error);

  // If we have a raw message, use it with appropriate title
  if (rawMessage) {
    return {
      title: errorCode ? `${defaultTitle} (${errorCode})` : defaultTitle,
      message: rawMessage,
      code: errorCode,
      details: validationDetails
    };
  }

  // Fallback to default
  return {
    title: defaultTitle,
    message: defaultMessage,
    code: errorCode
  };
};

/**
 * Extracts just the error message (backward compatibility)
 *
 * This function is provided for backward compatibility with existing code.
 * For new code, prefer using `formatError` which provides both title and message.
 *
 * @param error - The error to extract message from
 * @param defaultMessage - Default message if no error message is found (default: 'An error occurred')
 * @returns The error message string
 *
 * @example
 * ```ts
 * const message = extractErrorMessage(error, 'Default message');
 * showSnackbar('Error', message, 'error');
 * ```
 */
export const extractErrorMessage = (error: unknown, defaultMessage: string = 'An error occurred'): string => {
  const formatted = formatError(error, defaultMessage);
  return formatted.message;
};
